import React, { useEffect, useMemo, useState } from "react";
import api from "../utils/api";

/**
 * Earnings Predictor (calendar + scanner)
 * - Loads today's earnings (and selectable upcoming day)
 * - Shows Premarket / Postmarket / Unknown buckets
 * - "Analyze all (ranked)" merges scan results into the SAME cards and reorders (no duplicate list)
 * - Per-row Analyze pulls detailed predictor for that ticker
 *
 * Notes:
 * - Prices are filled via /api/quotes?symbols=... if your backend provides it.
 * - Options metrics in scan results require user_id + connected Tradier on backend; this component will
 *   try to pass user_id from localStorage when available (user_id/email).
 */
export default function EarningsPredictor() {
    const [selectedDate, setSelectedDate] = useState(() => {
        const d = new Date();
        // YYYY-MM-DD in local time (backend defaults ET if omitted, but we send explicit date)
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
    });

    const [calendarBuckets, setCalendarBuckets] = useState({ pre: [], post: [], unknown: [] });
    const [loadingCal, setLoadingCal] = useState(false);

    const [rankMode, setRankMode] = useState(false);
    const [scanLoading, setScanLoading] = useState(false);
    const [scanMeta, setScanMeta] = useState(null); // optional summary from API

    const [ticker, setTicker] = useState("");
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const userId = useMemo(() => {
        // Try common keys (adjust to your auth system as needed)
        return (
            localStorage.getItem("user_id") ||
            localStorage.getItem("email") ||
            localStorage.getItem("userEmail") ||
            ""
        );
    }, []);

    const flatten = (buckets) => [
        ...(buckets.pre || []).map((x) => ({ ...x, bucket: "pre" })),
        ...(buckets.post || []).map((x) => ({ ...x, bucket: "post" })),
        ...(buckets.unknown || []).map((x) => ({ ...x, bucket: "unknown" })),
    ];

    const fetchQuotesFor = async (items) => {
        // If your backend has /api/quotes and your items don't already include price,
        // fill them in (cached backend is recommended).
        const syms = items
            .map((x) => x.symbol)
            .filter(Boolean)
            .slice(0, 800); // safety cap

        if (syms.length === 0) return null;

        try {
            const res = await api.get(`/api/quotes?symbols=${encodeURIComponent(syms.join(","))}`);
            return res.data || null;
        } catch {
            return null;
        }
    };

    const applyQuoteMap = (buckets, quoteMap) => {
        if (!quoteMap) return buckets;
        const patchItem = (it) => {
            const q = quoteMap[it.symbol];
            if (!q) return it;
            // supports either {price: 123} or {last: 123} or {c: 123}
            const px =
                q.price ?? q.last ?? q.c ?? (typeof q === "number" ? q : null);
            return px != null ? { ...it, price: px } : it;
        };
        return {
            pre: (buckets.pre || []).map(patchItem),
            post: (buckets.post || []).map(patchItem),
            unknown: (buckets.unknown || []).map(patchItem),
        };
    };

    const loadCalendar = async () => {
        setLoadingCal(true);
        setRankMode(false);
        setScanMeta(null);
        setDetail(null);

        try {
            const res = await api.get(`/api/earnings/calendar?date=${encodeURIComponent(selectedDate)}&days=0`);
            const day = (res.data?.results || {})[selectedDate] || { pre: [], post: [], unknown: [] };
            let buckets = {
                pre: day.pre || [],
                post: day.post || [],
                unknown: day.unknown || [],
            };

            // Fill prices (best-effort)
            const allItems = flatten(buckets);
            const quoteMap = await fetchQuotesFor(allItems);
            buckets = applyQuoteMap(buckets, quoteMap);

            setCalendarBuckets(buckets);
        } catch (e) {
            console.error("calendar load failed:", e?.response?.data || e.message);
            alert(e?.response?.data?.error || "Failed to load earnings calendar");
            setCalendarBuckets({ pre: [], post: [], unknown: [] });
        } finally {
            setLoadingCal(false);
        }
    };

    useEffect(() => {
        loadCalendar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDate]);

    const allCards = useMemo(() => flatten(calendarBuckets), [calendarBuckets]);

    const sortRanked = (items) => {
        // Bullish first, then Mixed, then Bearish; within each, higher absolute score first
        const dirRank = (d) => (d === "bullish" ? 0 : d === "mixed" ? 1 : d === "bearish" ? 2 : 1);
        return [...items].sort((a, b) => {
            const ra = dirRank(a.direction || a.sentiment);
            const rb = dirRank(b.direction || b.sentiment);
            if (ra !== rb) return ra - rb;
            const sa = Number(a.score ?? 0);
            const sb = Number(b.score ?? 0);
            // higher score first (bullish); if bearish group, more negative should rise, so sort by absolute
            return Math.abs(sb) - Math.abs(sa);
        });
    };

    const mergeScanIntoBuckets = (scanResults) => {
        const map = new Map();
        (scanResults || []).forEach((r) => {
            if (r?.ticker) map.set(r.ticker, r);
            if (r?.symbol) map.set(r.symbol, r);
        });

        const mergeItem = (it) => {
            const r = map.get(it.symbol);
            if (!r) return it;
            return {
                ...it,
                // fields from scanner
                direction: r.direction || r.sentiment || it.direction,
                sentiment: r.sentiment || it.sentiment,
                score: r.score ?? it.score,
                confidence: r.confidence ?? it.confidence,
                bias: r.bias || it.bias,
                optionsMarket: r.optionsMarket || it.optionsMarket,
                reasons: r.reasons || it.reasons,
            };
        };

        return {
            pre: (calendarBuckets.pre || []).map(mergeItem),
            post: (calendarBuckets.post || []).map(mergeItem),
            unknown: (calendarBuckets.unknown || []).map(mergeItem),
        };
    };

    const analyzeAll = async (includeOptions = true) => {
        setScanLoading(true);
        setDetail(null);

        try {
            const qs = new URLSearchParams();
            qs.set("date", selectedDate);
            qs.set("bucket", "all");
            qs.set("include_options", includeOptions ? "1" : "0");
            if (userId) qs.set("user_id", userId);

            const res = await api.get(`/api/earnings/analyze-day?${qs.toString()}`);

            const results = res.data?.results || [];
            setScanMeta({
                count: results.length,
                includeOptions: includeOptions ? "yes" : "no",
            });

            const merged = mergeScanIntoBuckets(results);
            setCalendarBuckets(merged);
            setRankMode(true);
        } catch (e) {
            console.error("scan failed:", e?.response?.data || e.message);
            alert(e?.response?.data?.error || "Failed to analyze earnings day");
        } finally {
            setScanLoading(false);
        }
    };

    const analyzeOne = async (sym) => {
        const t = (sym || ticker || "").trim().toUpperCase();
        if (!t) {
            alert("Type a ticker first (ex: AAPL)");
            return;
        }
        setDetailLoading(true);
        try {
            const qs = new URLSearchParams();
            qs.set("ticker", t);
            qs.set("date", selectedDate);
            if (userId) qs.set("user_id", userId);
            const res = await api.get(`/api/earnings/predict?${qs.toString()}`);
            setDetail(res.data);
            setTicker(t);
        } catch (e) {
            console.error("predict failed:", e?.response?.data || e.message);
            alert(e?.response?.data?.error || "Failed to fetch earnings analysis");
        } finally {
            setDetailLoading(false);
        }
    };

    const BucketHeader = ({ title, count }) => (
        <div className="flex items-center justify-between mt-6">
            <h2 className="text-lg font-semibold">{title}</h2>
            <div className="text-xs text-gray-400">{count} tickers</div>
        </div>
    );

    const Pill = ({ children }) => (
        <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-900 border border-gray-700 text-xs text-gray-200">
            {children}
        </span>
    );

    const DirPill = ({ dir }) => {
        const d = (dir || "").toLowerCase();
        const cls =
            d === "bullish"
                ? "text-green-300"
                : d === "bearish"
                    ? "text-red-300"
                    : "text-yellow-300";
        return <span className={`font-semibold ${cls}`}>{(d || "mixed").toUpperCase()}</span>;
    };

    const Card = ({ item }) => {
        const bucketLabel =
            item.bucketLabel ||
            (item.bucket === "pre" ? "Premarket" : item.bucket === "post" ? "Postmarket" : "Unknown");

        const px = item.price != null && !Number.isNaN(Number(item.price)) ? Number(item.price) : null;

        const dir = item.direction || item.sentiment;

        return (
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-2">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <div className="text-lg font-semibold">{item.symbol}</div>
                        <div className="text-sm text-gray-300">{item.name || ""}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                        <Pill>{bucketLabel}</Pill>
                        {px != null && <Pill>${px.toFixed(2)}</Pill>}
                    </div>
                </div>

                <div className="flex flex-wrap gap-2">
                    {item.epsEst != null && <Pill>EPS est: {item.epsEst}</Pill>}
                    {item.revEst != null && <Pill>Rev est: {item.revEst}</Pill>}
                    {dir && <Pill>Dir: <DirPill dir={dir} /></Pill>}
                    {item.confidence != null && <Pill>Conf: {item.confidence}%</Pill>}
                    {item.score != null && <Pill>Score: {Number(item.score).toFixed(2)}</Pill>}
                </div>

                {item.optionsMarket?.implied_move_pct != null && (
                    <div className="text-xs text-gray-300">
                        Options: ±{item.optionsMarket.implied_move_pct}% • Skew:{" "}
                        <b>{item.optionsMarket.skew_bias || "neutral"}</b>
                        {item.optionsMarket.put_call_oi_ratio != null
                            ? ` (PCR ${item.optionsMarket.put_call_oi_ratio})`
                            : ""}
                    </div>
                )}

                <div className="flex gap-2 pt-1">
                    <button
                        className="px-3 py-2 rounded bg-blue-600 hover:bg-blue-500 text-sm"
                        onClick={() => analyzeOne(item.symbol)}
                        disabled={detailLoading}
                    >
                        {detailLoading && ticker === item.symbol ? "Analyzing..." : "Analyze"}
                    </button>
                </div>
            </div>
        );
    };

    const rankedList = useMemo(() => sortRanked(allCards), [allCards]);

    return (
        <div className="max-w-5xl mx-auto space-y-4">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold">Earnings Predictor</h1>
                    <div className="text-sm text-gray-400">
                        Calendar view first, then optional ranking + options metrics.
                    </div>
                </div>

                <div className="flex flex-wrap gap-2 items-center">
                    <div className="flex items-center gap-2">
                        <label className="text-xs text-gray-300">Date</label>
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="px-2 py-2 rounded bg-gray-800 border border-gray-700 text-sm"
                        />
                    </div>

                    <button
                        onClick={() => analyzeAll(false)}
                        className="px-3 py-2 rounded bg-gray-700 hover:bg-gray-600 text-sm"
                        disabled={loadingCal || scanLoading}
                        title="Fast scan (no options)"
                    >
                        {scanLoading ? "Scanning..." : "Analyze all (fast)"}
                    </button>

                    <button
                        onClick={() => analyzeAll(true)}
                        className="px-3 py-2 rounded bg-purple-700 hover:bg-purple-600 text-sm"
                        disabled={loadingCal || scanLoading}
                        title="Scan + options implied move/skew (requires user_id + Tradier on backend)"
                    >
                        {scanLoading ? "Scanning..." : "Analyze all (ranked)"}
                    </button>

                    <button
                        onClick={() => setRankMode((v) => !v)}
                        className="px-3 py-2 rounded bg-gray-900 border border-gray-700 hover:bg-gray-800 text-sm"
                        disabled={loadingCal}
                        title="Toggle ranked display"
                    >
                        {rankMode ? "Show buckets" : "Show ranked"}
                    </button>
                </div>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-3">
                <div className="flex flex-wrap gap-2 items-center">
                    <input
                        value={ticker}
                        onChange={(e) => setTicker(e.target.value.toUpperCase())}
                        placeholder="Search ticker (e.g., AAPL)"
                        className="flex-1 min-w-[220px] px-3 py-2 rounded bg-gray-800 border border-gray-700"
                    />
                    <button
                        onClick={() => analyzeOne(ticker)}
                        className="bg-blue-600 px-4 py-2 rounded"
                        disabled={detailLoading}
                    >
                        {detailLoading ? "Analyzing..." : "Analyze ticker"}
                    </button>
                </div>

                {loadingCal && <div className="text-gray-400">Loading earnings calendar…</div>}
                {!loadingCal && scanMeta && (
                    <div className="text-xs text-gray-400">
                        Scan complete: {scanMeta.count} tickers • options: {scanMeta.includeOptions}
                    </div>
                )}
            </div>


            {/* Detail panel */}
            {detail && (
                <div className="bg-gray-800 border border-gray-700 rounded p-4 space-y-2">
                    <div className="text-lg font-semibold">{detail.ticker} Analysis</div>

                    <div>
                        Earnings:{" "}
                        <b>
                            {detail.earnings?.date || "Unknown"}{" "}
                            {detail.earnings?.bucketLabel ? `(${detail.earnings.bucketLabel})` : ""}
                        </b>
                    </div>

                    <div>
                        Sentiment:{" "}
                        <span
                            className={`font-bold ${detail.sentiment === "bullish"
                                ? "text-green-400"
                                : detail.sentiment === "bearish"
                                    ? "text-red-400"
                                    : "text-yellow-400"
                                }`}
                        >
                            {(detail.sentiment || "mixed").toUpperCase()}
                        </span>
                        {detail.direction && (
                            <span className="text-gray-300"> • Direction: {detail.direction}</span>
                        )}
                    </div>

                    <div>Confidence: <b>{detail.confidence}%</b></div>

                    {detail.bias && (
                        <div className="border-t border-gray-700 pt-2">
                            <div>📈 Stock Bias: <b>{detail.bias.stock}</b></div>
                            <div>🧾 Options Bias: <b>{detail.bias.options}</b></div>
                        </div>
                    )}

                    {detail.optionsMarket?.implied_move_pct != null && (
                        <div className="text-sm text-gray-300 border-t border-gray-700 pt-2">
                            Options: ±{detail.optionsMarket.implied_move_pct}% • Skew:{" "}
                            <b>{detail.optionsMarket.skew_bias || "neutral"}</b>
                            {detail.optionsMarket.put_call_oi_ratio != null
                                ? ` (PCR ${detail.optionsMarket.put_call_oi_ratio})`
                                : ""}
                            {detail.optionsMarket.iv_front != null && (
                                <>
                                    {" • "}IV: <b>{detail.optionsMarket.iv_front}</b>
                                    {detail.optionsMarket.iv_next != null ? ` → ${detail.optionsMarket.iv_next}` : ""}
                                </>
                            )}
                        </div>
                    )}

                    {Array.isArray(detail.reasons) && detail.reasons.length > 0 && (
                        <div className="text-sm text-gray-300 border-t border-gray-700 pt-2">
                            <div className="font-semibold text-gray-200 mb-1">Why</div>
                            <ul className="list-disc ml-5 space-y-1">
                                {detail.reasons.slice(0, 8).map((r, i) => (
                                    <li key={i}>{r}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}


            {/* Cards */}
            {!rankMode ? (
                <div className="space-y-3">
                    <BucketHeader title="Premarket" count={(calendarBuckets.pre || []).length} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {(calendarBuckets.pre || []).map((it) => (
                            <Card key={`${it.symbol}-pre`} item={it} />
                        ))}
                    </div>

                    <BucketHeader title="Postmarket" count={(calendarBuckets.post || []).length} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {(calendarBuckets.post || []).map((it) => (
                            <Card key={`${it.symbol}-post`} item={it} />
                        ))}
                    </div>

                    <BucketHeader title="Unknown time" count={(calendarBuckets.unknown || []).length} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {(calendarBuckets.unknown || []).map((it) => (
                            <Card key={`${it.symbol}-unknown`} item={it} />
                        ))}
                    </div>
                </div>
            ) : (
                <div className="space-y-3">
                    <BucketHeader title="Ranked (same cards, reordered)" count={rankedList.length} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {rankedList.map((it) => (
                            <Card key={`${it.symbol}-rank`} item={it} />
                        ))}
                    </div>
                </div>
            )}           
        </div>
    );
}
